import { ProductPriceBasisType, QualityAdjustmentType, QualityFormulaType } from '@local/lib';
import { Badge, Button, Card, Icon, Input } from '@salesforce/design-system-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { SpecDefinition, SpecState } from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';
import {
  getContractValue,
  getInputError,
  type PlaygroundInputs,
  type PlaygroundRow,
  previewPlayground,
} from '../playgroundPreview';
import {
  CONTRACT_PRICE_COMPONENTS,
  CONTRACT_UNIT_OF_MEASURE,
  FORMULA_TYPES,
  getFormulaAdjustmentCategory,
  getSpecUnit,
} from '../qualityFormulaMetadata';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import './playgroundScenario.css';

const currency = (value: number) =>
  `${value < 0 ? '−' : ''}$${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
const quantity = (value: number) =>
  value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 1 });

const initialInputs = (
  specs: SpecDefinition[],
  states: Record<string, SpecState>,
  qty: number,
  price: number,
): PlaygroundInputs => ({
  actual: Object.fromEntries(specs.map(spec => [spec.key, String(getContractValue(states[spec.key]))])),
  components: Object.fromEntries(
    specs.map(spec => [
      spec.key,
      Object.fromEntries(CONTRACT_PRICE_COMPONENTS.map(component => [component.key, String(component.value)])),
    ]),
  ),
  price: String(price),
  quantity: String(qty),
});

interface NumericFieldProps {
  centered?: boolean;
  inlineUnits?: boolean;
  label: string;
  onChange: (value: string) => void;
  percent?: boolean;
  prefix?: string;
  suffix?: string;
  value: string;
}

const NumericField: React.FC<NumericFieldProps> = ({
  centered = false,
  inlineUnits = false,
  label,
  onChange,
  percent = false,
  prefix,
  suffix,
  value,
}) => (
  <div className={`qfw-scenario-field${inlineUnits ? ' qfw-scenario-field-inline-units' : ''}`}>
    <Input
      errorText={getInputError(value, percent)}
      fixedTextLeft={prefix}
      fixedTextRight={suffix}
      label={label}
      onChange={(_event, data) => {
        onChange(data.value);
      }}
      step={0.01}
      styleInput={{ fontVariantNumeric: 'tabular-nums', textAlign: centered ? 'center' : 'right' }}
      type="number"
      value={value}
    />
  </div>
);

const impactText = (row: PlaygroundRow) => {
  if (row.impact === 0) return 'No change';
  if (row.kind === 'quantity')
    return `${row.impact < 0 ? '−' : '+'}${quantity(Math.abs(row.impact))} ${CONTRACT_UNIT_OF_MEASURE}`;
  return `${currency(row.impact)}/${CONTRACT_UNIT_OF_MEASURE}`;
};

const statusText = (status: PlaygroundRow['status']) => {
  if (status === 'success') return 'In spec';
  if (status === 'warning') return 'Near limit';
  return 'Out of spec';
};

const impactCellText = (result: PlaygroundRow | undefined, invalid: boolean) => {
  if (!result || invalid) return '—';
  if (result.impact === 0) return result.kind === 'quantity' ? `0.0 ${CONTRACT_UNIT_OF_MEASURE}` : '$0.00';
  return impactText(result);
};
const impactTypeText = (formulaType: QualityFormulaType) =>
  getFormulaAdjustmentCategory(formulaType) === QualityAdjustmentType.Quantity ? 'Quantity Impact' : 'Price Impact';
const changeClass = (value: number) => {
  if (value < 0) return 'qfw-scenario-danger';
  if (value > 0) return 'qfw-scenario-gain';
  return 'slds-text-color_weak';
};
const changeArrow = (value: number) => {
  if (value < 0) return '▼';
  if (value > 0) return '▲';
  return '—';
};

interface PlaygroundScenarioProps {
  enabledSpecs: SpecDefinition[];
  /** Hide the card's own icon/title — used where an outer frame already shows a "Playground" heading. */
  showHeading?: boolean;
}

export const PlaygroundScenario: React.FC<PlaygroundScenarioProps> = ({ enabledSpecs, showHeading = true }) => {
  const { deliveredQuantity, specStates, tradePrice } = useQualityFormulaStore(
    useShallow(store => ({
      deliveredQuantity: store.deliveredQuantity,
      specStates: store.specStates,
      tradePrice: store.tradePrice,
    })),
  );
  const [inputs, setInputs] = useState(() => initialInputs(enabledSpecs, specStates, deliveredQuantity, tradePrice));
  const [settled, setSettled] = useState(inputs);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettled(inputs);
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [inputs]);

  const update = (patch: Partial<PlaygroundInputs>) => {
    setInputs(current => ({ ...current, ...patch }));
  };
  const updateActual = (key: string, value: string) => {
    setInputs(current => ({ ...current, actual: { ...current.actual, [key]: value } }));
  };
  const updateComponent = (spec: string, key: string, value: string) => {
    setInputs(current => ({
      ...current,
      components: { ...current.components, [spec]: { ...current.components[spec], [key]: value } },
    }));
  };

  const invalidShared = Boolean(getInputError(inputs.price) ?? getInputError(inputs.quantity));
  const invalidComponents = enabledSpecs.some(spec => {
    const state = specStates[spec.key];
    return (
      state.formulaType === QualityFormulaType.Percentage &&
      state.priceBasis.type === ProductPriceBasisType.PriceComponents &&
      CONTRACT_PRICE_COMPONENTS.some(
        component => getInputError(inputs.components[spec.key][component.key]) !== undefined,
      )
    );
  });
  const preview = useMemo(() => {
    if (getInputError(settled.price) ?? getInputError(settled.quantity)) return null;
    return previewPlayground(enabledSpecs, specStates, settled);
  }, [enabledSpecs, specStates, settled]);
  const rows = preview ? preview.rows : [];
  const startPrice = Number(settled.price);
  const startQty = Number(settled.quantity);
  const priceDelta = (preview ? preview.finalPrice : startPrice) - startPrice;
  const qtyDelta = (preview ? preview.finalQuantity : startQty) - startQty;

  return (
    <div className="qfw-scenario-layout">
      <div className="slds-box qfw-scenario-frame">
        <Card
          className="qfw-scenario-card"
          headerActions={
            <Button
              label="Reset to Contract Values"
              onClick={() => {
                setInputs(initialInputs(enabledSpecs, specStates, deliveredQuantity, tradePrice));
              }}
              variant="outline-brand"
            />
          }
          heading={
            showHeading ? (
              <span className="qfw-scenario-heading">
                <Icon category={IconCategory.Utility} name="path_experiment" size="small" />
                <span>Playground</span>
              </span>
            ) : (
              // `Card` falls back to its own default "Related Items" placeholder heading when
              // `heading` is `undefined` — pass explicit text instead so the compact Manage
              // Templates frame (which hides the icon/"Playground" title because its outer
              // collapsible frame already shows one) doesn't surface that unrelated default.
              'Test'
            )
          }
        >
          <p className="slds-text-body_small slds-text-color_weak slds-m-bottom_small">
            Try sample values; configured terms stay unchanged. Illustrative estimate until pricing engine integration.
          </p>
          <div className="qfw-scenario-shared">
            <NumericField
              inlineUnits
              label="Delivered Quantity"
              onChange={value => {
                update({ quantity: value });
              }}
              suffix={CONTRACT_UNIT_OF_MEASURE}
              value={inputs.quantity}
            />
            <NumericField
              inlineUnits
              label="Contract Price"
              onChange={value => {
                update({ price: value });
              }}
              prefix="$"
              suffix={`/${CONTRACT_UNIT_OF_MEASURE}`}
              value={inputs.price}
            />
          </div>
          <div className="qfw-scenario-scroll">
            <table className="slds-table slds-table_bordered slds-table_cell-buffer qfw-scenario-table">
              <thead>
                <tr>
                  <th scope="col">Spec</th>
                  <th scope="col">Formula</th>
                  <th scope="col">Contract</th>
                  <th scope="col">Test Value</th>
                  <th scope="col">Status</th>
                  <th scope="col">Impact</th>
                  <th scope="col">
                    <span className="slds-assistive-text">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {enabledSpecs.map(spec => {
                  const state = specStates[spec.key];
                  const value = inputs.actual[spec.key] ?? '';
                  const result = rows.find(row => row.spec.key === spec.key);
                  const usesComponents =
                    state.formulaType === QualityFormulaType.Percentage &&
                    state.priceBasis.type === ProductPriceBasisType.PriceComponents;
                  const usesDeemedPrice =
                    state.formulaType === QualityFormulaType.Percentage &&
                    state.priceBasis.type === ProductPriceBasisType.DeemedPrice;
                  const components = usesComponents ? CONTRACT_PRICE_COMPONENTS : [];
                  const componentError = components.some(item => getInputError(inputs.components[spec.key][item.key]));
                  const status = result ? result.status : undefined;
                  const basisOpen = open[spec.key] ?? usesComponents;
                  return (
                    <Fragment key={spec.key}>
                      <tr>
                        <th scope="row">
                          {spec.label}
                          {getSpecUnit(spec.key) === '%' ? ' %' : ''}
                        </th>
                        <td>
                          <Badge
                            color="light"
                            content={
                              FORMULA_TYPES.find(item => item.value === state.formulaType)?.label ?? state.formulaType
                            }
                          />
                        </td>
                        <td className="qfw-scenario-number">{getContractValue(state).toLocaleString('en-US')}</td>
                        <td className="qfw-scenario-test-value">
                          <NumericField
                            centered
                            label={`${spec.label} test value`}
                            onChange={next => {
                              updateActual(spec.key, next);
                            }}
                            percent={getSpecUnit(spec.key) === '%'}
                            value={value}
                          />
                        </td>
                        <td>
                          {status && !componentError ? (
                            <span className={`qfw-scenario-status qfw-scenario-${status}`}>{statusText(status)}</span>
                          ) : (
                            <span className="slds-text-color_weak">—</span>
                          )}
                        </td>
                        <td className="qfw-scenario-number">
                          <span className={changeClass(result && !componentError ? result.impact : 0)}>
                            {impactCellText(result, componentError)}
                          </span>
                          <span className="qfw-scenario-impact-type">{impactTypeText(state.formulaType)}</span>
                        </td>
                        <td>
                          {usesComponents || usesDeemedPrice ? (
                            <Button
                              assistiveText={{
                                icon: `${basisOpen ? 'Hide' : 'Show'} ${spec.label} price basis`,
                              }}
                              iconCategory="utility"
                              iconName={basisOpen ? 'chevronup' : 'chevrondown'}
                              onClick={() => {
                                setOpen(current => ({ ...current, [spec.key]: !basisOpen }));
                              }}
                              variant="icon"
                            />
                          ) : null}
                        </td>
                      </tr>
                      {usesComponents && basisOpen ? (
                        <tr>
                          <td className="qfw-scenario-breakdown-cell" colSpan={7}>
                            <div className="qfw-scenario-breakdown">
                              <strong>Price Component Breakdown — Applies to {spec.label} only</strong>
                              <div className="qfw-scenario-components">
                                {components.map(item => (
                                  <NumericField
                                    key={item.key}
                                    label={`${item.label} ($)`}
                                    onChange={next => {
                                      updateComponent(spec.key, item.key, next);
                                    }}
                                    prefix="$"
                                    value={inputs.components[spec.key][item.key]}
                                  />
                                ))}
                              </div>
                              <strong>
                                Test basis ={' '}
                                {componentError
                                  ? '—'
                                  : currency(
                                      components.reduce(
                                        (sum, item) => sum + item.sign * Number(inputs.components[spec.key][item.key]),
                                        0,
                                      ),
                                    )}{' '}
                                /MT
                              </strong>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      {usesDeemedPrice && basisOpen ? (
                        <tr>
                          <td className="qfw-scenario-detail" colSpan={7}>
                            <strong>Deemed Price — Applies to {spec.label} only</strong>
                            <p>Price basis = {currency(state.priceBasis.deemedPrice)} /MT</p>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <div className="slds-box qfw-scenario-frame qfw-scenario-result-frame">
        <Card className="qfw-scenario-result" heading="Result">
          <p className="slds-text-body_small slds-text-color_weak">Updates Live</p>
          {invalidShared || invalidComponents ? (
            <p className="slds-text-color_error" role="status">
              {invalidShared
                ? 'Fix shared inputs to update the result.'
                : 'Invalid spec values are excluded from the result.'}
            </p>
          ) : null}
          <div className="qfw-scenario-metrics">
            <div className="qfw-scenario-metric">
              <span>Final Total</span>
              <strong>{preview && !invalidShared ? currency(preview.finalPrice * preview.finalQuantity) : '—'}</strong>
              <span className="qfw-scenario-was slds-text-color_weak">
                was {preview && !invalidShared ? currency(startPrice * startQty) : '—'}
              </span>
            </div>
            <div className="qfw-scenario-metric">
              <span>Final Price</span>
              <strong>{preview && !invalidShared ? `${currency(preview.finalPrice)} /MT` : '—'}</strong>
              <span className={changeClass(priceDelta)}>
                {invalidShared ? '—' : `${changeArrow(priceDelta)} ${currency(Math.abs(priceDelta))}/MT`}
              </span>
            </div>
            <div className="qfw-scenario-metric">
              <span>Final Quantity</span>
              <strong>{preview && !invalidShared ? `${quantity(preview.finalQuantity)} MT` : '—'}</strong>
              <span className={changeClass(qtyDelta)}>
                {invalidShared ? '—' : `${changeArrow(qtyDelta)} ${quantity(Math.abs(qtyDelta))} MT`}
              </span>
            </div>
          </div>
          <h3 className="slds-text-heading_small">Impact by spec</h3>
          <ul className="qfw-scenario-impact-list">
            {[...rows]
              .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))
              .map(row => (
                <li key={row.spec.key}>
                  <span>
                    {row.spec.label}
                    <span className="qfw-scenario-impact-type">
                      {impactTypeText(specStates[row.spec.key].formulaType)}
                    </span>
                  </span>
                  <span className={changeClass(row.impact)}>{impactText(row)}</span>
                </li>
              ))}
          </ul>
        </Card>
      </div>
    </div>
  );
};
